import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { lcMsgError } from "@/utils/util";
import { LOGIN_URL } from "@/config/index";
import useUserStore from "@/stores/modules/user";
import useTaskStore from "@/stores/modules/task";
import { getToken } from "@/utils/storage";
import router from "@/routers/index";
import type { InternalAxiosRequestConfig } from "axios";

// 环境变量
const { VITE_SERVER } = import.meta.env;

// 常量定义
const HTTP_STATUS = {
  SUCCESS: 200,
  CREATED: 201,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
} as const;

const SUCCESS_CODES = ["OK", 200, 0];

// 错误消息映射
const ERROR_MESSAGE_MAP: Record<number, string> = {
  400: "错误请求",
  401: "未授权，请重新登录",
  403: "对不起，您没有权限访问",
  404: "请求错误,未找到请求路径",
  405: "请求方法未允许",
  408: "请求超时",
  500: "服务器又偷懒了，请重试",
  501: "网络未实现",
  502: "网络错误",
  503: "服务不可用",
  504: "网络超时",
  505: "http版本不支持该请求",
};

// 接口定义
export interface Result<T = any> {
  code: number;
  msg: string;
  message: string;
  data: T;
  status?: string | number;
  [k: string]: any;
}

export interface RequestConfig extends AxiosRequestConfig {
  skipErrorHandler?: boolean;
}

// 创建axios实例
export const createAxiosInstance = (config: AxiosRequestConfig = {}): AxiosInstance => {
  const baseConfig: AxiosRequestConfig = {
    baseURL: VITE_SERVER,
    timeout: 120000,
    headers: {
      "Content-Type": "application/json",
    },
    ...config,
  };

  const instance = axios.create(baseConfig);

  // 请求拦截器
  instance.interceptors.request.use(handleRequestSuccess, handleRequestError);

  // 响应拦截器
  instance.interceptors.response.use(handleResponseSuccess, handleResponseError);

  return instance;
};

// 请求成功处理
const handleRequestSuccess = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const taskStore = useTaskStore();
  const token = getToken();

  config.headers = {
    ...config.headers,
    TaskId: taskStore.taskId || "",
    Subtaskid: taskStore.subtaskId || "",
    ...(token && { Logintoken: token }),
  };

  return config;
};

// 请求错误处理
const handleRequestError = (error: any): Promise<never> => {
  const errorInfo = {
    data: { msg: "服务器异常，请联系管理员" },
    ...error,
  };
  return Promise.reject(errorInfo);
};

// 响应成功处理
const handleResponseSuccess = (response: AxiosResponse): any => {
  const { config, data } = response;

  // 处理blob响应类型
  if (config.responseType === "blob") {
    return response;
  }

  const status = response.status;
  const code = data?.code;

  // 成功状态处理
  if (SUCCESS_CODES.includes(status)) {
    return data;
  }

  // 未授权处理
  if (status === 401 || code === 401) {
    handleUnauthorized();
    return Promise.reject(data);
  }

  // 其他错误状态
  if (!(config as RequestConfig).skipErrorHandler) {
    lcMsgError(data?.message || "服务器连接超时");
  }

  return Promise.reject(data);
};

// 响应错误处理
const handleResponseError = (error: any): Promise<never> => {
  const { response, config } = error;

  if (response?.status) {
    const message = ERROR_MESSAGE_MAP[response.status] || `连接错误${response.status}`;

    if (!(config as RequestConfig)?.skipErrorHandler) {
      lcMsgError(message);
    }

    // 未授权特殊处理
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      handleUnauthorized();
    }
  } else {
    const message = "连接到服务器失败";
    if (!(config as RequestConfig)?.skipErrorHandler) {
      lcMsgError(message);
    }
  }

  return Promise.reject(error);
};

// 未授权处理
const handleUnauthorized = (): void => {
  const userStore = useUserStore();
  userStore.setToken("");
  lcMsgError("登录身份过期，请重新登录");
  router.replace(LOGIN_URL);
};

// 创建默认实例
const defaultInstance = createAxiosInstance();

// 基础请求方法
export const request = <T = Result>(config: RequestConfig): Promise<T> => {
  return defaultInstance.request(config);
};

export const get = <T = Result>(url: string, params?: object): Promise<T> => {
  return defaultInstance.get(url, { params });
};

export const post = <T = Result>(url: string, data?: any, config?: RequestConfig): Promise<T> => {
  return defaultInstance.post(url, data, config);
};

export const put = <T = Result>(url: string, data?: any, config?: RequestConfig): Promise<T> => {
  return defaultInstance.put(url, data, config);
};

export const del = <T = Result>(url: string, config?: RequestConfig): Promise<T> => {
  return defaultInstance.delete(url, config);
};

// 特殊请求方法
export const upload = <T = Result>(url: string, data?: FormData, config?: RequestConfig): Promise<T> => {
  return defaultInstance.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    ...config,
  });
};

export const postForm = <T = Result>(url: string, data?: Record<string, any>, config?: RequestConfig): Promise<T> => {
  const formData = new URLSearchParams();

  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });
  }

  return defaultInstance.post(url, formData, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    ...config,
  });
};

// 专用请求方法（使用原生axios）
export const exportExcel = <T = Result>(url: string, params?: object): Promise<T> => {
  const taskStore = useTaskStore();
  return axios.get(`${VITE_SERVER}${url}`, {
    params,
    headers: {
      Accept: "application/vnd.ms-excel",
      Authorization: `Bearer ${getToken()}`,
      TaskId: taskStore.taskId || "",
    },
    responseType: "blob",
  });
};

export const getCloudPicBlob = <T = Result>(url: string, params?: object): Promise<T> => {
  const taskStore = useTaskStore();
  return axios.post(`${import.meta.env.VITE_WEB_BASE_API}${url}`, params, {
    headers: {
      Accept: "image/png",
      Logintoken: getToken(),
      TaskId: taskStore.taskId || "",
    },
    responseType: "blob",
  });
};

export const getPicBlob = <T = Result>(url: string): Promise<T> => {
  const taskStore = useTaskStore();
  return axios.get(url, {
    headers: {
      Accept: "image/png",
      Logintoken: getToken(),
      TaskId: taskStore.taskId || "",
    },
    responseType: "blob",
  });
};

export const createHttp = (config: AxiosRequestConfig = {}) => {
  const instance = createAxiosInstance(config);

  const request = <T = Result>(config: RequestConfig): Promise<T> => {
    return instance.request(config);
  };

  const get = <T = Result>(url: string, params?: object): Promise<T> => {
    return instance.get(url, { params });
  };

  const post = <T = Result>(url: string, data?: any, config?: RequestConfig): Promise<T> => {
    return instance.post(url, data, config);
  };

  const put = <T = Result>(url: string, data?: any, config?: RequestConfig): Promise<T> => {
    return instance.put(url, data, config);
  };

  const del = <T = Result>(url: string, config?: RequestConfig): Promise<T> => {
    return instance.delete(url, config);
  };

  return {
    request,
    get,
    post,
    put,
    delete: del,
  };
};

// 默认导出
export default {
  request,
  get,
  post,
  put,
  delete: del,
  upload,
  postForm,
  instance: defaultInstance,
};
